import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, BadgeCheck } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const gstStatusFor = (d) => {
  const t = (d.taxpayer_type || "").toLowerCase();
  if (d.is_sez) return "sez";
  if (t.includes("composition")) return "composition";
  return "registered";
};

export const applyGstinLookup = (f, d) => ({
  ...f,
  gstin: d.gstin || f.gstin,
  name: d.trade_name || d.legal_name || f.name,
  display_name: d.trade_name || d.legal_name || f.display_name,
  legal_name: d.legal_name || f.legal_name,
  trade_name: d.trade_name || f.trade_name,
  pan: d.pan || f.pan,
  state: d.state || f.state,
  state_code: d.state_code || f.state_code,
  address: d.address || f.address,
  address_line1: d.address_line1 || f.address_line1,
  address_line2: d.address_line2 || f.address_line2,
  city: d.city || f.city,
  pincode: d.pincode || f.pincode,
  gst_status: gstStatusFor(d),
  is_sez_customer: !!d.is_sez,
  sez_verified_at: d.is_sez ? new Date().toISOString() : f.sez_verified_at,
  gst_portal_status: d.status || "",
  constitution: d.constitution || "",
  taxpayer_type: d.taxpayer_type || "",
  registration_date: d.registration_date || "",
});

const Fact = ({ k, v, testId }) => (
  <div><div className="text-[10px] font-mono uppercase text-muted-foreground">{k}</div>
    <div className="text-xs mt-0.5" data-testid={testId}>{v || "—"}</div></div>
);

export const GstinQuickFetch = ({ form, setForm }) => {
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);
  const g = (form.gstin || "").trim();

  const fetchInfo = async () => {
    setBusy(true);
    try {
      const r = await api.get("/gstin/lookup", { params: { gstin: g } });
      if (!r.data.valid) { setInfo(null); toast.error(r.data.error || "Invalid GSTIN"); return; }
      setForm((f) => applyGstinLookup(f, r.data));
      setInfo(r.data);
      toast.success("Details fetched from GST portal");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fetch failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border rounded-sm p-3 bg-muted/30 mt-4" data-testid="gstin-quick-fetch">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
        Quick fill from GST portal — enter GSTIN to auto-fill name, PAN, address, state & GST status
      </div>
      <div className="flex gap-2">
        <Input value={form.gstin} maxLength={15} placeholder="15-char GSTIN"
          onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase().trim() })}
          onKeyDown={(e) => { if (e.key === "Enter" && g.length === 15) fetchInfo(); }}
          className="rounded-sm font-mono" data-testid="quick-gstin-input" />
        <Button type="button" onClick={fetchInfo} disabled={busy || g.length !== 15}
          className="rounded-sm whitespace-nowrap gap-1.5" data-testid="quick-gstin-fetch-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          {busy ? "Fetching…" : "Fetch from GST portal"}
        </Button>
      </div>
      {info && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 border-t pt-3" data-testid="gstin-fetch-result">
          <div className="col-span-2 sm:col-span-4 flex items-center gap-1.5 text-xs font-medium">
            <BadgeCheck className="w-4 h-4 text-emerald-600" /> {info.legal_name}
          </div>
          <Fact k="Status" v={info.status} testId="gstin-fetch-status" />
          <Fact k="Taxpayer type" v={info.taxpayer_type} testId="gstin-fetch-type" />
          <Fact k="Constitution" v={info.constitution} testId="gstin-fetch-constitution" />
          <Fact k="Registered on" v={info.registration_date} testId="gstin-fetch-regdate" />
          <div className="col-span-2 sm:col-span-4"><Fact k="Principal place of business" v={info.address} testId="gstin-fetch-address" /></div>
        </div>
      )}
    </div>
  );
};
